import { useState } from 'react';
import { cn, getInitials, stringToColor } from '@/lib/utils';

interface AvatarProps {
  src?: string;
  userId?: string; // Optional user ID for constructing avatar URL
  name?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const sizes = {
  xs: 'w-6 h-6 text-[10px]',
  sm: 'w-8 h-8 text-xs',
  md: 'w-10 h-10 text-sm',
  lg: 'w-12 h-12 text-base',
  xl: 'w-16 h-16 text-lg',
};

/**
 * Transform avatar source to a usable URL
 * - If it starts with 'avatars/' it's a storage key - convert to API endpoint
 * - If it starts with 'http' or '/' it's already a URL - use as-is
 * - If userId is provided and src looks like a storage key, use the user avatar endpoint
 */
function getAvatarSrc(src: string | undefined, userId: string | undefined): string | undefined {
  if (!src) return undefined;
  
  // Already a full URL or relative URL
  if (src.startsWith('http') || src.startsWith('/api')) {
    return src;
  }
  
  // It's a storage key (e.g., 'avatars/user-id/timestamp.jpg')
  if (src.startsWith('avatars/')) {
    // Extract user ID from storage key if not provided
    const match = src.match(/^avatars\/([^/]+)\//);
    const extractedUserId = userId || (match ? match[1] : undefined);
    
    if (extractedUserId) {
      return `/api/v1/users/${extractedUserId}/avatar?v=${Date.now()}`;
    }
  }
  
  // Fallback - return as-is (might be an external URL without http prefix)
  return src;
}

export function Avatar({ src, userId, name, size = 'md', className }: AvatarProps) {
  const [imgError, setImgError] = useState(false);
  const initials = getInitials(name);
  const bgColor = stringToColor(name);
  const avatarSrc = getAvatarSrc(src, userId);

  // Show image if we have a valid src and no error
  if (avatarSrc && !imgError) {
    return (
      <img
        src={avatarSrc}
        alt={name || 'User'}
        className={cn('avatar', sizes[size], className)}
        onError={() => setImgError(true)}
      />
    );
  }
  
  // Show initials fallback
  return (
    <div
      className={cn('avatar', sizes[size], className)}
      style={{ backgroundColor: bgColor }}
    >
      <span className="text-white font-semibold">{initials}</span>
    </div>
  );
}

interface AvatarGroupProps {
  users: Array<{ name: string; avatarUrl?: string }>;
  max?: number;
  size?: 'xs' | 'sm' | 'md';
}

export function AvatarGroup({ users, max = 4, size = 'sm' }: AvatarGroupProps) {
  const visible = users.slice(0, max);
  const remaining = users.length - max;
  
  return (
    <div className="flex -space-x-2">
      {visible.map((user, i) => (
        <Avatar
          key={i}
          src={user.avatarUrl}
          name={user.name}
          size={size}
          className="ring-2 ring-white"
        />
      ))}
      {remaining > 0 && (
        <div className={cn(
          'avatar ring-2 ring-white bg-grey-200 text-grey-600',
          sizes[size]
        )}>
          +{remaining}
        </div>
      )}
    </div>
  );
}


